import Papa from "papaparse";

// Client-side mirror of server/utils/gearSubmission.js so users see problems
// in the preview before submitting. The server re-validates everything.

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const parseNumber = (value) => {
  if (typeof value === "number") return value;
  const cleaned = String(value).trim().replace(/[$,]/g, "").replace(/\s*[a-z°]+$/i, "");
  return cleaned === "" ? NaN : Number(cleaned);
};

const isHttpUrl = (value) => {
  try {
    return ["http:", "https:"].includes(new URL(String(value).trim()).protocol);
  } catch (error) {
    return false;
  }
};

export const validateSubmissionRow = (fields, row) => {
  const errors = [];
  fields.forEach((field) => {
    const value = row[field.key];
    if (isBlank(value)) {
      if (field.required) errors.push(`${field.label} is required`);
      return;
    }
    if (field.type === "number") {
      const number = parseNumber(value);
      if (!Number.isFinite(number) || number < 0) errors.push(`${field.label} must be a number`);
      else if (field.key === "weight" && number === 0) errors.push("Weight must be greater than 0");
    } else if (field.type === "url" && !isHttpUrl(value)) {
      errors.push(`${field.label} must be a valid http(s) URL`);
    } else if (
      field.type === "enum" &&
      !field.values.some((option) => option.toLowerCase() === String(value).trim().toLowerCase())
    ) {
      errors.push(`${field.label} must be one of: ${field.values.join(", ")}`);
    }
  });
  return errors;
};

// Template headers are the field keys, which parse unambiguously.
export const buildCsvTemplate = (fields) =>
  `${Papa.unparse({ fields: fields.map((field) => field.key), data: [] })}\n`;

export const downloadCsvTemplate = (gearType, fields) => {
  const blob = new Blob([buildCsvTemplate(fields)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `packwizard-${gearType}-template.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const normalizeHeader = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Parses an uploaded CSV into rows keyed by field key. Headers may be the
// template keys or the human labels ("Weight (g)"); unknown columns are
// reported back so users notice typos.
export const parseSubmissionCsv = (file, fields) =>
  new Promise((resolve, reject) => {
    const headerToKey = {};
    fields.forEach((field) => {
      headerToKey[normalizeHeader(field.key)] = field.key;
      headerToKey[normalizeHeader(field.label)] = field.key;
    });
    const unknownColumns = new Set();
    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (header) => {
        const key = headerToKey[normalizeHeader(header)];
        if (!key && header.trim()) unknownColumns.add(header.trim());
        return key || `__unknown__${header}`;
      },
      complete: ({ data, errors }) => {
        const rows = data.map((row) =>
          Object.fromEntries(Object.entries(row).filter(([key]) => !key.startsWith("__unknown__")))
        );
        resolve({
          rows,
          unknownColumns: [...unknownColumns],
          parseErrors: errors.map((error) => `Row ${error.row + 2}: ${error.message}`),
        });
      },
      error: reject,
    });
  });

// Gear page path for a published item, by submission gear type
export const GEAR_PAGE_PATH = {
  tents: "tent",
  sleepingPads: "sleeping-pad",
  sleepingBags: "sleeping-bag",
  packs: "backpacks",
  insulatedJackets: "insulated-jacket",
  stoves: "stove",
};
